import { useEffect, useState } from "react";

/**
 * True once the viewport is at least `px` wide. Used to choose between genuinely different
 * image sets per breakpoint (e.g. Home hero's two-panel aerial split vs. the single flattened
 * phone composite, per Hero Background Design.md Section 4.3) so only one set of <img>s is ever
 * mounted at a time -- CSS display:none alone would still download the hidden set's src.
 *
 * SSR-safe default: this app is CSR-only (Technical Blueprint D8), so `window` is always present
 * by the time this runs; the guard is only for test/module-eval edge cases.
 */
export default function useMinWidth(px) {
  const query = `(min-width: ${px}px)`;
  const getMatch = () => (typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(query).matches
    : false);

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else if (mql.addListener) mql.addListener(handler); // older Safari
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else if (mql.removeListener) mql.removeListener(handler);
    };
  }, [query]);

  return matches;
}
